import { lazy, Suspense, type ReactNode } from "react";
import type { TopicId } from "../types/dashboard";
import { ConnectedChargersRow } from "../cards/ConnectedChargersRow";
import { ErrorBoundary } from "../components/ErrorBoundary";
import {
  groupCardsByRow,
  type CardDefinition,
} from "./cardRegistry";
import { DASHBOARD_FREQ_RANGES } from "../context/FreqRangeContext";

const FreqHeroChart = lazy(() =>
  import("../cards/FreqHeroChart").then((m) => ({ default: m.FreqHeroChart })),
);
const WsConnectionsChart = lazy(() =>
  import("../cards/WsConnectionsChart").then((m) => ({ default: m.WsConnectionsChart })),
);
const OcppTrafficCharts = lazy(() =>
  import("../cards/OcppTrafficCharts").then((m) => ({ default: m.OcppTrafficCharts })),
);

function ChartFallback() {
  return (
    <div
      className="mobile-chart-fallback flex items-center justify-center rounded-lg border border-border-subtle bg-card text-text-secondary"
      style={{ fontSize: "var(--font-body)" }}
    >
      Loading chart…
    </div>
  );
}

function withSuspense(node: ReactNode) {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ChartFallback />}>{node}</Suspense>
    </ErrorBoundary>
  );
}

export const MOBILE_CARD_REGISTRY: CardDefinition[] = [
  {
    id: "freq-hero-mobile",
    topic: "dashboard",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<FreqHeroChart compact availableRanges={DASHBOARD_FREQ_RANGES} />),
  },
  {
    id: "ws-chart-dash-mobile",
    topic: "dashboard",
    row: "charts",
    type: "chart",
    order: 2,
    render: () => withSuspense(<WsConnectionsChart compact title="WS connections (ocpp-edge)" />),
  },
  {
    id: "chargers-dash-mobile",
    topic: "dashboard",
    row: "tiles",
    rowLabel: "Connected chargers",
    type: "tile-row",
    order: 3,
    render: () => <ConnectedChargersRow compact />,
  },
  {
    id: "ocpp-ws-hero-mobile",
    topic: "ocpp",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<WsConnectionsChart tall compact title="WS connections (ocpp-edge)" />),
  },
  {
    id: "ocpp-traffic-mobile",
    topic: "ocpp",
    row: "charts",
    rowLabel: "OCPP messages & Ethernet traffic",
    type: "chart",
    order: 2,
    render: () => withSuspense(<OcppTrafficCharts />),
  },
  {
    id: "freq-hero-topic-mobile",
    topic: "frequency",
    row: "hero",
    type: "hero",
    order: 1,
    render: () => withSuspense(<FreqHeroChart compact />),
  },
];

export function mobileCardsForTopic(topic: TopicId): CardDefinition[] {
  return MOBILE_CARD_REGISTRY.filter((c) => c.topic === topic).sort(
    (a, b) => a.order - b.order,
  );
}

export { groupCardsByRow };
